import { formatDateLabel, formatNumberLabel, formatPercent, toArray, toBoolean, toNullableNumber, toNumberValue, toStringValue } from '@/features/charting-next/adapters/chartingAdapterUtils';

export function normalizeChartingStock(item = {}, index = 0) {
    const ticker = toStringValue(item.ticker || item.symbol || item.id || item.nom, 'N/D').toUpperCase();
    const displayName = toStringValue(item.nom && item.nom !== '---' ? item.nom : item.name || item.title || ticker, ticker);
    const folderNames = toArray(item.folder_name || item.folderNames || item.folders)
        .map((value) => toStringValue(value))
        .filter(Boolean);
    const chart = item.chart && typeof item.chart === 'object' ? item.chart : {};
    const companyDescription = toStringValue(item.description || item.descripcion);
    const priceValue = item.valor_actual ?? item.current_price ?? item.price ?? item.close ?? item.last_price;
    const stopValue = item.out_price ?? item.stop_price;
    const supportValue = item.soport ?? item.support_price ?? item.resistance;
    const entryTypeLabel = toStringValue(item.entry_type_name || item.tipus_entrada || item.entry_type);
    const normalizedEntryTypeLabel = entryTypeLabel.toUpperCase();
    const rawEntryPrice = item.real_entry ?? item.in_price ?? item.entry_price;
    const entryPriceLabel = formatNumberLabel(rawEntryPrice) || toStringValue(rawEntryPrice);
    const direction = toStringValue(item.direccio || item.direction);
    const directionValue = (() => {
        const value = toNullableNumber(item.d ?? item.direccio ?? item.direction);

        return value === 0 || value === 1 ? value : null;
    })();
    const supportPriceValue = toNullableNumber(supportValue);
    const stopPriceValue = toNullableNumber(stopValue);
    const resistancePriceValue =
        normalizedEntryTypeLabel.startsWith('RR') || normalizedEntryTypeLabel.startsWith('RS')
            ? toNullableNumber(item.real_entry ?? item.entry_price ?? item.entrada ?? item.resistance)
            : toNullableNumber(item.r ?? item.resistance);
    const trendLineStartPriceValue = toNullableNumber(item.lt_ini ?? item.ltPriceIni);
    const trendLineEndPriceValue = toNullableNumber(item.lt_fi ?? item.ltPriceEnd);
    const trendLineStartIndex = toNullableNumber(item.lt_pos_chart_ini ?? item.ltPosIni);
    const trendLineEndIndex = toNullableNumber(item.lt_pos_chart_fi ?? item.ltPosEnd);
    const trendLineStartDate = toStringValue(item.lt_pos_ini || item.ltPosIniDate);
    const trendLineEndDate = toStringValue(item.lt_pos_fi || item.ltPosEndDate);
    const trendPointPriceValue = normalizedEntryTypeLabel === 'LT' ? toNullableNumber(item.plt ?? item.real_entry ?? item.entry_price ?? item.entrada) : null;
    const riskRewardPointPriceValue = toNullableNumber(item.prr ?? item.rr_point ?? item.real_rr_entry);
    const entryVariantKey = [
        toStringValue(item.entry_type || item.tipus_entrada || item.entry_type_name),
        toStringValue(rawEntryPrice),
        toStringValue(stopValue),
        toStringValue(supportValue),
        direction,
        toStringValue(item.data_analisi)
    ]
        .filter(Boolean)
        .join('|') || `${ticker || 'stock'}-${index}`;

    return {
        id: `${ticker || 'stock'}-${index}`,
        ticker,
        displayName,
        description: [toStringValue(item.country), toStringValue(item.index_valor || item.exchange), toStringValue(item.sector)].filter(Boolean).join(' · '),
        companyDescription,
        companyName: displayName,
        country: toStringValue(item.country),
        indexName: toStringValue(item.index_valor || item.exchange),
        currency: toStringValue(item.divisa || item.currency),
        currencyQuoteLabel: formatNumberLabel(item.cotitzacio_divisa) || toStringValue(item.cotitzacio_divisa),
        sector: toStringValue(item.sector),
        industry: toStringValue(item.industria || item.industry),
        entryTypeLabel,
        entryVariantKey,
        entryVariantBaseLabel: entryTypeLabel || 'Entrada',
        commentsCount: toNumberValue(item.total_comments || item.comment_count, 0),
        priceLabel: formatNumberLabel(priceValue) || toStringValue(priceValue),
        currentPriceValue: toNullableNumber(priceValue),
        entryPriceLabel,
        entryPriceValue: toNullableNumber(item.real_entry ?? item.in_price ?? item.entry_price),
        stopPriceLabel: formatNumberLabel(stopValue) || toStringValue(stopValue),
        stopPriceValue,
        supportPriceLabel: formatNumberLabel(supportValue) || toStringValue(supportValue),
        supportPriceValue,
        resistancePriceValue,
        amountLabel: formatNumberLabel(item.amount) || toStringValue(item.amount),
        amountValue: toNullableNumber(item.amount),
        instrumentNumberLabel: formatNumberLabel(item.instrument_number) || toStringValue(item.instrument_number),
        instrumentNumberValue: toNullableNumber(item.instrument_number),
        entryDistanceLabel: Number.isFinite(Number(item.entry_distance)) ? formatPercent(Number(item.entry_distance), 2) : toStringValue(item.entry_distance),
        entryDistanceValue: toNullableNumber(item.entry_distance),
        profitability: toNullableNumber(item.profitability_interval ?? item.tae_lt),
        profitabilityLabel: Number.isFinite(Number(item.profitability_interval ?? item.tae_lt)) ? formatPercent(Number(item.profitability_interval ?? item.tae_lt), Math.abs(Number(item.profitability_interval ?? item.tae_lt)) > 1000 ? 0 : 1) : '',
        firstBarDateLabel: toStringValue(item.fecha_primera_barra),
        firstBarDateFormatted: formatDateLabel(item.fecha_primera_barra),
        supportDateLabel: toStringValue(item.data_soport),
        supportDateFormatted: formatDateLabel(item.data_soport),
        analysisDateLabel: toStringValue(item.data_analisi),
        analysisDateFormatted: formatDateLabel(item.data_analisi),
        waveNumberLabel: toStringValue(item.num_ona_actual),
        avgDividendsLabel: formatNumberLabel(item.avg_dividends) || toStringValue(item.avg_dividends),
        avgDividendsValue: toNullableNumber(item.avg_dividends),
        riskRewardLabel: formatNumberLabel(item.risk_reward) || toStringValue(item.risk_reward),
        riskRewardValue: toNullableNumber(item.risk_reward),
        platinumScore: toNullableNumber(item.platinum_compatibility),
        platinumScoreLabel: item.platinum_compatibility !== null && item.platinum_compatibility !== undefined ? `${Math.round(Number(item.platinum_compatibility) * 100)}` : '',
        userCompatibility: toNullableNumber(item.user_platinum_compatibility),
        userCompatibilityLabel: item.user_platinum_compatibility !== null && item.user_platinum_compatibility !== undefined ? `${Math.round(Number(item.user_platinum_compatibility) * 100)}` : '',
        isEureker: item.isEureker !== false,
        chartPath: toStringValue(chart.url || item.chart_url),
        chartImage: toStringValue(chart.image || item.chart_image),
        folderNames,
        isFavorite: toBoolean(item.favorites, folderNames.length > 0),
        direction,
        cfdMode: toNumberValue(item.activat_dif, 0),
        cfdMargin: toNullableNumber(item.cfd_margin),
        cfdMarginLabel: Number.isFinite(Number(item.cfd_margin)) ? formatPercent(Number(item.cfd_margin), 2) : '',
        forumThreadId: toStringValue(item.nid || item.thread_id),
        chartOverlays: {
            priceLines: {
                support:
                    supportPriceValue !== null
                        ? {
                              price: supportPriceValue,
                              originDate: toStringValue(item.data_soport),
                              label: 'Soporte',
                              color: '#dc2626',
                              lineStyle: 0
                          }
                        : null,
                stop:
                    stopPriceValue !== null
                        ? {
                              price: stopPriceValue,
                              label: 'Stop',
                              color: '#dc2626',
                              lineStyle: 2
                          }
                        : null,
                resistance:
                    resistancePriceValue !== null
                        ? {
                              price: resistancePriceValue,
                              label: 'Resistencia',
                              color: '#16a34a',
                              lineStyle: 0
                          }
                        : null
            },
            trendLine:
                trendLineStartPriceValue !== null &&
                trendLineEndPriceValue !== null &&
                directionValue !== null &&
                ((trendLineStartDate && trendLineEndDate) || (trendLineStartIndex !== null && trendLineEndIndex !== null))
                    ? {
                          direction: directionValue,
                          startDate: trendLineStartDate,
                          endDate: trendLineEndDate,
                          startIndex: trendLineStartIndex,
                          endIndex: trendLineEndIndex,
                          startPrice: trendLineStartPriceValue,
                          endPrice: trendLineEndPriceValue
                      }
                    : null,
            trendPoint:
                trendPointPriceValue !== null
                    ? {
                          price: trendPointPriceValue,
                          label: 'Punto LT',
                          color: '#0284c7'
                      }
                    : null,
            riskRewardPoint:
                riskRewardPointPriceValue !== null
                    ? {
                          price: riskRewardPointPriceValue,
                          label: 'Punto RR',
                          color: '#0284c7'
                      }
                    : null
        },
        raw: item
    };
}

export function normalizeChartingCollection(payload = [], entry = {}) {
    const items = toArray(payload).map((item, index) => normalizeChartingStock(item, index));

    return {
        entry,
        title: entry.title || 'Mercado',
        subtitle: entry.subtitle || '',
        items,
        totalItems: items.length,
        emptyMessage: entry.type === 'ticker' ? 'No se encontro ese ticker.' : 'No hay valores visibles para esta fuente.',
        raw: payload
    };
}
